import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import classes from '../SearchPage.module.scss'
import { RequestNonprofitPrompt } from '../../RequestNonprofit/RequestNonprofitPrompt/RequestNonprofitPrompt'

import {
  useAuth,
  useInfiniteQuery,
  useQuery,
  useQueryCountryCodeValidation,
  useRestrictedDonationConfig
} from '@percent/workplace-giving/common/hooks'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { Page } from '@percent/workplace-giving/common/components/Layout/Page'
import { SearchPageHeader } from '@percent/workplace-giving/common/components/SearchPageHeader/SearchPageHeader'
import { searchVerifiedOrganisations } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations'
import { OrgSearchFor } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import { mapInfiniteData } from '@percent/workplace-giving/utils/mapInfiniteData'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { OrganisationCardSkeleton } from '@percent/workplace-giving/common/components/OrganisationCard/OrganisationCardSkeleton'
import { OrganisationCard } from '@percent/workplace-giving/common/components/OrganisationCard/OrganisationCard'
import { useDonationForm } from '@percent/workplace-giving/common/hooks/useDonationForm/useDonationForm'
import { useColorTheme } from '@percent/workplace-giving/common/hooks/useColorTheme/useColorTheme'
import { useInfiniteScroll } from '@percent/workplace-giving/common/hooks/useInfiniteScroll/useInfinteScroll'
import { Flex, IllustratedMessage, Skeleton } from '@percent/lemonade'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import {
  getCountryCodeFromAuthState,
  getPartnerFromAuthState,
  getStateCodeFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { SwitchToGlobalResults } from '@percent/workplace-giving/common/components/SwitchToGlobalResults/SwitchToGlobalResults'
import { BlockedRegionsConfig } from '@percent/workplace-giving/api/donation/getDonationPartnerConfig/getDonationPartnerConfig.types'
import { logger } from '@percent/workplace-giving/utils/logger'
import { getActivityTags } from '@percent/workplace-giving/api/activityTags/getActivityTags'
import { FilterChipList } from '@percent/workplace-giving/common/components/FilterChipList/FilterChipList'
import {
  getActivityTagIcon,
  getActivityTagLabel
} from '@percent/workplace-giving/common/components/FilterChipList/activityTagIcons'

export function OrganisationsSearchPage() {
  const { t, i18n } = useTranslation()
  const { track } = useAnalytics()
  const navigate = useNavigate()
  const { state } = useAuth()
  const partner = getPartnerFromAuthState(state)!
  const [searchParams, setSearchParams] = useSearchParams()
  const { theme } = useColorTheme()
  const { handleDonate, DonationFormWrapper } = useDonationForm('regular')
  const has = usePermission()
  const donationsEnabled = has(scope('donation_link'))

  const query = searchParams.get('query') ?? undefined
  const code = searchParams.get('country') ?? undefined
  const selectedTagIds = searchParams.getAll('tags')

  const countryCode = useQueryCountryCodeValidation({ code })
  const prevCountryCodeRef = useRef<string>('')
  const { blockedRegions } = useRestrictedDonationConfig()
  const {
    data: activityTags = [],
    isLoading: isLoadingTags,
    isError: isActivityTagsError
  } = useQuery(['getActivityTags'], getActivityTags)

  useEffect(() => {
    if (isActivityTagsError) {
      logger(new Error('Failed to fetch activity tags'))
    }
  }, [isActivityTagsError])

  const sortedActivityTags = useMemo(
    () =>
      activityTags
        .map(tag => ({
          id: tag.id,
          label: getActivityTagLabel(t, tag.slug, tag.name),
          icon: getActivityTagIcon(tag.slug)
        }))
        .sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
    [activityTags, t, i18n.language]
  )

  // Blocked-country regions gate the donate action on each card (passed to OrganisationCard
  // below); they do NOT hide orgs from the list. Country exclusion for the result set itself is
  // applied backend-side by the search API, so any org it returns is meant to be shown here.
  const blockedCountryCodes = useMemo(
    () => blockedRegions.map((region: BlockedRegionsConfig) => region.countryCode),
    [blockedRegions]
  )

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isError } = useInfiniteQuery(
    [
      'searchVerifiedOrganisations',
      {
        query,
        pageSize: 10,
        countryCode,
        activityTags: selectedTagIds.length ? selectedTagIds : undefined,
        // Reached only via "See all" on the global-search nonprofits carousel, so it mirrors that
        // carousel's scope (OrganisationsResults uses DonationOrVolunteering) rather than donation-only.
        for: OrgSearchFor.DonationOrVolunteering
      }
    ],
    searchVerifiedOrganisations,
    {
      enabled: !!query,
      staleTime: 0
    }
  )

  const { ref } = useInfiniteScroll(fetchNextPage)

  const mappedData = mapInfiniteData(data)

  const results = useMemo(() => mappedData?.pages?.flat() ?? [], [mappedData])

  useEffect(() => {
    if (query === undefined) {
      navigate(AppRoute.HOME, { replace: true })
    }
  }, [query, navigate])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [searchParams])

  const onCountryChange = (country: string) => {
    setSearchParams(prev => {
      prev.set('country', country)

      return prev
    })
  }

  const onQueryChange = (q: string) => {
    setSearchParams(prev => {
      prev.set('query', q)

      return prev
    })
  }

  useEffect(() => {
    if (query?.trim().length) {
      track({
        event: 'Search Organisations',
        properties: {
          origin: 'Organisations Search Page',
          countryCode
        }
      })
    }
  }, [query, countryCode, track])

  const handleTagToggle = (tagId: string) => {
    setSearchParams(prev => {
      const current = prev.getAll('tags')
      const updated = current.includes(tagId) ? current.filter(id => id !== tagId) : [...current, tagId]

      // URLSearchParams has no "set all" — delete then re-append to replace repeated params
      prev.delete('tags')
      updated.forEach(id => prev.append('tags', id))

      return prev
    })
  }

  const switchToGlobal = () => {
    if (countryCode) {
      prevCountryCodeRef.current = countryCode
    }
    setSearchParams(prev => {
      prev.set('country', '')

      return prev
    })
    track({
      event: 'Donation Organisation Search Switch to Global',
      properties: {
        origin: 'Organisations Search Page'
      }
    })
  }

  const switchToCountry = () => {
    const prevCountryCode = prevCountryCodeRef.current

    if (!prevCountryCode) return

    setSearchParams(prev => {
      prev.set('country', prevCountryCode)

      return prev
    })
    prevCountryCodeRef.current = ''
    track({
      event: 'Donation Organisation Search Switch to User Country',
      properties: {
        origin: 'Organisations Search Page',
        countryCode: prevCountryCode
      }
    })
  }

  return (
    <Page testId="nonprofits-search-page" fullWidthContent>
      <SearchPageHeader
        query={query}
        countryCode={countryCode}
        onCountryChange={onCountryChange}
        onQueryChange={onQueryChange}
        breadcrumbsPageName={t('workplace_giving.search.breadcrumbs.nonprofits')}
      />
      {isLoadingTags && <Skeleton height={36} radius="xl" />}
      {activityTags.length > 0 && (
        <FilterChipList
          tags={sortedActivityTags}
          selectedIds={selectedTagIds}
          onToggle={handleTagToggle}
          testId="activity-tag-filters"
        />
      )}
      <Flex justify="flex-end" align="center" wrap="wrap" gap="xs" mb="md">
        <SwitchToGlobalResults
          totalResults={data && data.pages.length > 0 ? data.pages[0].totalResults : 0}
          currentCountryCode={countryCode}
          prevCountryCode={prevCountryCodeRef.current}
          switchToGlobal={switchToGlobal}
          switchToCountry={switchToCountry}
          isLoading={isFetching}
        />
        {!isFetching && <RequestNonprofitPrompt eventOrigin="Organisations Search Page" />}
      </Flex>
      {isLoading ? (
        <div className={classes.ResultsWrapper}>
          <OrganisationCardSkeleton />
          <OrganisationCardSkeleton />
          <OrganisationCardSkeleton />
          <OrganisationCardSkeleton />
          <OrganisationCardSkeleton />
        </div>
      ) : null}
      {!isLoading && !isError ? (
        <div className={classes.ResultsWrapper}>
          {results.map(organisation => (
            <OrganisationCard
              trackingOrigin="Search"
              key={organisation.organisationId}
              organisation={organisation}
              handleDonate={handleDonate}
              userOrigin={{
                country: getCountryCodeFromAuthState(state),
                state: getStateCodeFromAuthState(state)
              }}
              blockedCountryCodes={blockedCountryCodes}
            />
          ))}
          {hasNextPage && (
            <div ref={ref}>
              <OrganisationCardSkeleton />
            </div>
          )}
        </div>
      ) : null}
      <div className={classes.InfosWrapper}>
        {results.length === 0 && !hasNextPage && !isFetching && !isError ? (
          <IllustratedMessage
            illustration="no-results"
            title={t('workplace_giving.search.noResultsGlobalTitle', {
              searchTerm: query,
              interpolation: { escapeValue: false }
            })}
            description={t(
              donationsEnabled
                ? 'workplace_giving.search.noResultDescription'
                : 'workplace_giving.search.noResultDescriptionNoMatching',
              { partnerName: partner.name }
            )}
          />
        ) : null}
        {isError && (
          <IllustratedMessage
            illustration="general-error"
            title={t('workplace_giving.errors.somethingWentWrong')}
            description={t('workplace_giving.errors.tryReloadingThePage')}
          />
        )}
      </div>
      <DonationFormWrapper
        params={{
          primaryColor: theme.primary400
        }}
      />
    </Page>
  )
}
